import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { useApi, useMutation } from '../lib/use-api';
import { ErrorNotice } from './Feedback';
import type { ContentItem, ContentPage } from '../api/types';

interface GroupOption {
  principalId: string;
  displayName: string;
}

/**
 * Giá trị khởi tạo của form, khớp `ASSIGNMENT_DEFAULTS` bên delivery-api.
 *
 * Nhân đôi có chủ ý: app không import được từ service (khác tsconfig, khác
 * ranh giới đóng gói). Server VẪN là nơi ép — đây chỉ là chỗ đặt con trỏ ban
 * đầu cho giáo viên, lệch nhau thì form hiện sai mặc định chứ không tạo lỗ
 * hổng.
 */
const FORM_DEFAULTS = { maxAttempts: 3, allowResume: true } as const;

/**
 * Đổi giá trị ô `datetime-local` sang ISO-8601 có offset.
 *
 * Ô này cho chuỗi KHÔNG mang múi giờ (`2026-08-11T14:30`). `new Date()` đọc nó
 * theo giờ máy, nên `toISOString()` cho đúng mốc tuyệt đối mà giáo viên định —
 * server đổi lại về giờ địa phương của nó khi ghi DATETIME.
 */
function localInputToIso(value: string): string | null {
  if (!value) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** Ô số rỗng nghĩa là "không đặt" → `null`, không phải `0`. */
function optionalNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Đổi mốc DB về giá trị ô `datetime-local` — chiều ngược của `localInputToIso`.
 *
 * `dateStrings: true` nên mysql2 trả "2026-08-12 14:30:00" theo GIỜ SESSION của
 * DB, không phải ISO UTC. Ô `datetime-local` cũng muốn giờ địa phương, nên chỉ
 * cần đổi dấu cách thành "T" và cắt giây.
 *
 * KHÔNG đi qua `new Date()`: chuỗi không mang offset sẽ bị đọc theo giờ máy rồi
 * `toISOString()` trừ tiếp một lần nữa — mở dialog sửa mà không đụng gì rồi lưu
 * lại là mốc tự lùi 7 tiếng, mỗi lần lưu lùi thêm một lần.
 */
function dbTimeToLocalInput(value: string | null): string {
  if (!value) return '';
  return value.replace(' ', 'T').slice(0, 16);
}

/** Bài giao đang sửa — đủ field để đổ ngược vào form. */
export interface AssignmentFormValue {
  id: string;
  titleI18n: Record<string, string>;
  audience: string;
  audienceRefId: string | null;
  openAt: string | null;
  closeAt: string | null;
  maxAttempts: number | null;
  masteryScore: number | null;
  allowResume: boolean;
  timeLimitSec: number | null;
}

/**
 * Tạo và sửa bài giao.
 *
 * Bài giao trỏ vào NHÓM BẢN DỊCH của học liệu, không vào content cụ thể (§4.3):
 * một bài phục vụ được cả lớp tiếng Việt lẫn lớp tiếng Anh, và người học nhận
 * đúng biến thể theo ngôn ngữ của mình.
 *
 * Chế độ sửa KHÔNG cho đổi học liệu: đổi nghĩa là bài giao trỏ sang nội dung
 * khác trong khi các lượt đã làm vẫn gắn với nó, và báo cáo sẽ trộn hai bài
 * khác nhau vào một dòng. Cần bài khác thì tạo bài giao mới.
 */
export function AssignmentDialog({
  assignment,
  onClose,
  onSaved,
}: {
  /** Bỏ trống = tạo mới. */
  assignment?: AssignmentFormValue;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';
  const dialogRef = useRef<HTMLDialogElement>(null);
  const editing = assignment !== undefined;

  const [contentId, setContentId] = useState('');
  const [title, setTitle] = useState(
    assignment
      ? (assignment.titleI18n[locale] ?? Object.values(assignment.titleI18n)[0] ?? '')
      : '',
  );
  const [audience, setAudience] = useState<'group' | 'public'>(
    assignment?.audience === 'public' ? 'public' : 'group',
  );
  const [groupId, setGroupId] = useState(assignment?.audienceRefId ?? '');

  // Tuỳ chọn nâng cao. Giữ dạng CHUỖI đúng như người dùng gõ, chỉ đổi sang số
  // lúc gửi: ép Number() ngay khi gõ làm ô trống thành NaN và người dùng không
  // xoá được ký tự cuối. Chuỗi rỗng nghĩa là "không đặt" → gửi null.
  const [openAt, setOpenAt] = useState(dbTimeToLocalInput(assignment?.openAt ?? null));
  const [closeAt, setCloseAt] = useState(dbTimeToLocalInput(assignment?.closeAt ?? null));
  const [maxAttempts, setMaxAttempts] = useState(
    assignment ? (assignment.maxAttempts?.toString() ?? '') : String(FORM_DEFAULTS.maxAttempts),
  );
  const [timeLimitMin, setTimeLimitMin] = useState(
    assignment?.timeLimitSec ? String(Math.round(assignment.timeLimitSec / 60)) : '',
  );
  const [masteryPercent, setMasteryPercent] = useState(
    assignment?.masteryScore !== null && assignment?.masteryScore !== undefined
      ? String(Math.round(assignment.masteryScore * 100))
      : '',
  );
  const [allowResume, setAllowResume] = useState<boolean>(
    assignment?.allowResume ?? FORM_DEFAULTS.allowResume,
  );

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const contents = useApi<ContentPage>('/api/admin/content', { query: { limit: 100 } });
  const groups = useApi<{ items: GroupOption[] }>('/api/groups');

  // Chọn học liệu thì lấy luôn tiêu đề của nó làm tiêu đề bài giao — giáo viên
  // hiếm khi muốn đặt tên khác, và ô trống bắt gõ lại là ma sát vô ích.
  const chooseContent = (value: string): void => {
    setContentId(value);
    const picked = contents.data?.items.find((item: ContentItem) => item.contentId === value);
    if (picked && !title) setTitle(picked.title);
  };

  /**
   * Trả `true` khi lưu xong.
   *
   * PUT trả 204 → `apiFetch` cho `undefined`, mà `run` cũng trả `undefined`
   * khi lỗi. Và KHÔNG dùng được `create.error` ngay sau `await`: nó là React
   * state, lượt render hiện tại còn giữ giá trị cũ nên dialog sẽ đóng như thể
   * thành công trong khi lưu vừa thất bại.
   */
  const create = useMutation<void, boolean>(
    useCallback(async () => {
      const minutes = optionalNumber(timeLimitMin);
      const percent = optionalNumber(masteryPercent);

      const common = {
        // Giữ nguyên các bản dịch tiêu đề đã có, chỉ ghi đè ngôn ngữ đang xem:
        // sửa bài ở giao diện tiếng Việt mà xoá mất tiêu đề tiếng Anh là mất
        // dữ liệu im lặng — người học lớp EN sẽ thấy tiêu đề rỗng.
        titleI18n: { ...(assignment?.titleI18n ?? {}), [locale]: title.trim() },
        audience,
        ...(audience === 'group' ? { audienceRefId: groupId } : {}),
        openAt: localInputToIso(openAt),
        closeAt: localInputToIso(closeAt),
        maxAttempts: optionalNumber(maxAttempts),
        // Server lưu GIÂY, form nhập PHÚT — giáo viên nghĩ theo tiết học.
        timeLimitSec: minutes === null ? null : Math.round(minutes * 60),
        // Server lưu tỉ lệ 0–1 (khớp `score_scaled` của xAPI), form nhập %.
        masteryScore: percent === null ? null : percent / 100,
        allowResume,
      };

      if (assignment) {
        await apiFetch<void>(`/api/assignments/${assignment.id}`, {
          method: 'PUT',
          body: common,
        });
        return true;
      }

      const picked = contents.data?.items.find((item: ContentItem) => item.contentId === contentId);
      if (!picked) throw new Error('content chưa chọn');

      await apiFetch<{ id: string }>('/api/assignments', {
        method: 'POST',
        body: { translationGroupId: picked.translationGroupId, ...common },
      });
      return true;
    }, [
      assignment,
      contents.data,
      contentId,
      title,
      audience,
      groupId,
      locale,
      openAt,
      closeAt,
      maxAttempts,
      timeLimitMin,
      masteryPercent,
      allowResume,
    ]),
  );

  const submit = async (): Promise<void> => {
    if (await create.run()) onSaved();
  };

  const ready = (editing || contentId) && title.trim() && (audience === 'public' || groupId);

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <h2>{editing ? t('assignment.editHeading') : t('assignment.createHeading')}</h2>

      {/* Sửa thì không cho đổi học liệu — xem chú thích ở đầu component. */}
      {editing ? null : (
        <label className="field">
          <span>{t('assignment.content')}</span>
          <select value={contentId} onChange={(event) => chooseContent(event.target.value)}>
            <option value="">{t('assignment.choosePlaceholder')}</option>
            {(contents.data?.items ?? []).map((item: ContentItem) => (
              <option key={item.contentId} value={item.contentId}>
                {item.title}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="field">
        <span>{t('assignment.title')}</span>
        <input type="text" value={title} onChange={(event) => setTitle(event.target.value)} />
      </label>

      <label className="field">
        <span>{t('assignment.column.audience')}</span>
        <select
          value={audience}
          onChange={(event) => setAudience(event.target.value as 'group' | 'public')}
        >
          <option value="group">{t('assignment.audience.group')}</option>
          <option value="public">{t('assignment.audience.public')}</option>
        </select>
      </label>

      {audience === 'group' ? (
        <label className="field">
          <span>{t('assignment.group')}</span>
          <select value={groupId} onChange={(event) => setGroupId(event.target.value)}>
            <option value="">{t('assignment.choosePlaceholder')}</option>
            {(groups.data?.items ?? []).map((group) => (
              <option key={group.principalId} value={group.principalId}>
                {group.displayName}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {/* Thu gọn mặc định: giáo viên giao bài nhanh không phải đi qua 6 ô,
          nhưng vẫn chỉnh được khi cần. Dùng <details> chứ không tự dựng state
          đóng/mở — nó đã có sẵn hành vi bàn phím và được trình đọc màn hình
          thông báo đúng. */}
      {/* Mở sẵn khi sửa: sửa bài giao gần như luôn là để đổi hạn nộp hoặc số
          lần làm, tức đúng những ô nằm trong này. */}
      <details className="field-group" open={editing}>
        <summary>{t('assignment.advanced')}</summary>

        <label className="field">
          <span>{t('assignment.openAt')}</span>
          <input
            type="datetime-local"
            value={openAt}
            onChange={(event) => setOpenAt(event.target.value)}
          />
        </label>

        <label className="field">
          <span>{t('assignment.closeAt')}</span>
          <input
            type="datetime-local"
            value={closeAt}
            onChange={(event) => setCloseAt(event.target.value)}
          />
        </label>

        <label className="field">
          <span>{t('assignment.maxAttempts')}</span>
          <input
            type="number"
            min={1}
            step={1}
            value={maxAttempts}
            onChange={(event) => setMaxAttempts(event.target.value)}
          />
          <small className="hint">{t('assignment.maxAttemptsUnlimited')}</small>
        </label>

        <label className="field">
          <span>{t('assignment.timeLimitMin')}</span>
          <input
            type="number"
            min={1}
            step={1}
            value={timeLimitMin}
            onChange={(event) => setTimeLimitMin(event.target.value)}
          />
          <small className="hint">{t('assignment.timeLimitHint')}</small>
        </label>

        <label className="field">
          <span>{t('assignment.masteryScore')}</span>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            value={masteryPercent}
            onChange={(event) => setMasteryPercent(event.target.value)}
          />
          <small className="hint">{t('assignment.masteryHint')}</small>
        </label>

        <label className="field field--inline">
          <input
            type="checkbox"
            checked={allowResume}
            onChange={(event) => setAllowResume(event.target.checked)}
          />
          <span>{t('assignment.allowResume')}</span>
        </label>
      </details>

      {create.error ? <ErrorNotice error={create.error} /> : null}

      <footer className="dialog__footer">
        <button type="button" onClick={() => dialogRef.current?.close()}>
          {t('common:action.cancel')}
        </button>
        <button type="button" disabled={create.pending || !ready} onClick={() => void submit()}>
          {editing ? t('common:action.save') : t('common:action.create')}
        </button>
      </footer>
    </dialog>
  );
}
